// @ts-nocheck
/**
 * PBX Management List — refresh, MODULE_ACL.PBX_MANAGEMENT gate, bulk delete.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, Download, Plus, RefreshCw, Server } from 'lucide-react';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { API_MANAGE_DIALER_LISTING, API_MANAGE_DIALER_DELETE } from '@/service/url';
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { getUser } from '@/utilities/setting';
import Loader from '@/components/partials/components/Loader';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const initialsOf = (name?: string) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const DialerListing = () => {
  const navigate = useNavigate();
  const [dialers, setDialers] = useState<any[]>([]);
  const [selectedDialers, setSelectedDialers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [moduleOperations, setModuleOperations] = useState<any>({});
  const [exportBusy, setExportBusy] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperAdmin(true);
      if (user?.acl_settings) {
        try {
          setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
        } catch {}
      }
    }
    const onAclRefreshed = () => {
      const u = getUser();
      if (u?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof u.acl_settings.module_operations === 'string'
              ? parseModuleOperations(u.acl_settings.module_operations)
              : u.acl_settings.module_operations;
          setModuleOperations(parsed);
        } catch {}
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchDialers = async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const response: any = await getApiCall(API_MANAGE_DIALER_LISTING, {});
      setDialers(response || []);
    } catch (err) {
      setDialers([]);
      if (isRefresh) toast.error(friendlyApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDialers();
  }, []);

  const canRead = !!moduleOperations?.[MODULE_ACL.PBX_MANAGEMENT]?.read;
  const canAdd = isSuperAdmin;
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;

  if (!canRead) {
    return (
      <FillPage>
        <div className="my-12 text-center text-base">
          No read permission found in assigned ACL
        </div>
      </FillPage>
    );
  }

  const filtered = dialers;

  const toggleRow = (id: any) => {
    setSelectedDialers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleAll = () => {
    const ids = filtered.map((d: any) => d.dialer_id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedDialers.includes(id));
    if (allSelected) setSelectedDialers((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedDialers((prev) => Array.from(new Set([...prev, ...ids])));
  };

  async function executeDelete() {
    setDeleting(true);
    try {
      if (bulkDeleteMode) {
        await Promise.all(
          selectedDialers.map((id) => postApiCall(API_MANAGE_DIALER_DELETE, { id })),
        );
        toast.success('Selected PBX(s) deleted successfully');
        setSelectedDialers([]);
      } else {
        await postApiCall(API_MANAGE_DIALER_DELETE, { id: deleteTarget });
        toast.success('PBX deleted successfully');
        setSelectedDialers((prev) => prev.filter((id) => id !== deleteTarget));
      }
      await fetchDialers();
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setBulkDeleteMode(false);
    }
  }

  async function handleExport() {
    setExportBusy(true);
    try {
      exportToCSV({
        fileName: 'pbx-list',
        data: filtered,
        columns: [
          { header: 'PBX ID', accessor: 'dialer_id' },
          { header: 'PBX Name', accessor: 'dialer_name' },
          { header: 'IP', accessor: 'ip' },
          { header: 'Port', accessor: 'port' },
          { header: 'Gateway', accessor: (row: any) => row.dialerServer?.server_name || '-' },
          {
            header: 'Trunk',
            accessor: (row: any) => {
              const names = (row.trunkMappings || [])
                .map((tm: any) => tm.trunk?.name)
                .filter(Boolean);
              if (names.length) return names.join(', ');
              return row.dialerTrunk?.name || '-';
            },
          },
          { header: 'Location', accessor: (row: any) => row.dialerLocation?.location_name || '-' },
          {
            header: 'Active CLI',
            accessor: (row: any) => row.active_cli_display || row.active_cli || 0,
          },
          { header: 'Active Calls', accessor: 'active_calls' },
          { header: 'Total Channels', accessor: 'total_channels' },
          { header: 'Channel Reduction', accessor: 'channel_reduction' },
          { header: 'CPS', accessor: (row: any) => row.cps ?? row.cps_reduction ?? 0 },
        ],
      });
    } finally {
      setExportBusy(false);
    }
  }

  if (loading && dialers.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((d: any) => selectedDialers.includes(d.dialer_id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedDialers.includes(row.original.dialer_id)}
          onCheckedChange={() => toggleRow(row.original.dialer_id)}
          aria-label="Select row"
        />
      ),
    },
    {
      id: 'dialer_name',
      header: 'PBX Name',
      accessorFn: (row: any) => row.dialer_name || '',
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary ring-1 ring-inset ring-primary/20">
              {initialsOf(d.dialer_name)}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">
                {d.dialer_name || '—'}
              </div>
              <div className="text-[11px] text-muted-foreground tabular-nums">
                ID {d.dialer_id}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'ip_port',
      header: 'IP · Port',
      accessorFn: (row: any) => `${row.ip || ''}:${row.port || ''}`,
      cell: ({ row }) => (
        <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
          {row.original.ip || '—'}
          {row.original.port ? `:${row.original.port}` : ''}
        </span>
      ),
    },
    {
      id: 'gateway',
      header: 'Gateway',
      accessorFn: (row: any) => row.dialerServer?.server_name || '',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.dialerServer?.server_name || '—'}
        </span>
      ),
    },
    {
      id: 'trunk',
      header: 'Trunk',
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        const names = (d.trunkMappings || [])
          .map((tm: any) => tm.trunk?.name)
          .filter(Boolean);
        const label = names.length ? names.join(', ') : d.dialerTrunk?.name || '—';
        return (
          <span className="line-clamp-1 max-w-[160px] text-sm text-foreground" title={label}>
            {label}
          </span>
        );
      },
    },
    {
      id: 'location',
      header: 'Location',
      accessorFn: (row: any) => row.dialerLocation?.location_name || '',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.dialerLocation?.location_name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'active_cli',
      header: 'Active CLI',
      cell: ({ row }) => (
        <span className="tabular-nums text-foreground">
          {row.original.active_cli_display || row.original.active_cli || 0}
        </span>
      ),
    },
    {
      accessorKey: 'active_calls',
      header: 'Active Calls',
      cell: ({ row }) => {
        const n = Number(row.original.active_calls ?? 0);
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset tabular-nums ' +
              (n > 0
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-muted/60 text-muted-foreground ring-border/60')
            }
          >
            {n}
          </span>
        );
      },
    },
    {
      accessorKey: 'total_channels',
      header: 'Total Ch.',
      cell: ({ row }) => (
        <span className="tabular-nums text-foreground">
          {row.original.total_channels ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'channel_reduction',
      header: 'Avail. Ch.',
      cell: ({ row }) => (
        <span className="tabular-nums text-muted-foreground">
          {row.original.channel_reduction ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'cps',
      header: 'CPS',
      cell: ({ row }) => (
        <span className="tabular-nums text-foreground">
          {row.original.cps ?? row.original.cps_reduction ?? 0}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/create-dialer/${row.original.dialer_id}`);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="Edit"
              aria-label="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original.dialer_id);
                setBulkDeleteMode(false);
                setDeleteOpen(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="PBX Management"
        description="Provisioned dialer / PBX instances and their assigned call centers."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDialers({ isRefresh: true })}
              disabled={refreshing || loading}
              className="gap-1.5"
            >
              <RefreshCw className={'h-3.5 w-3.5 ' + (refreshing ? 'animate-spin' : '')} />
              Refresh
            </Button>
            {dialers.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exportBusy}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button
                size="sm"
                onClick={() => navigate(`/${ROUTES.MANAGE_DIALERS.CREATE}`)}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Create PBX
              </Button>
            )}
          </>
        }
      />

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {canDelete && selectedDialers.length > 0 && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              setBulkDeleteMode(true);
              setDeleteOpen(true);
            }}
            className="gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete Selected ({selectedDialers.length})
          </Button>
        )}
        <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground shadow-neu-raised">
          <Server className="h-3.5 w-3.5 text-primary" />
          <span>
            <span className="font-semibold text-foreground tabular-nums">
              {dialers.length}
            </span>{' '}
            PBX(s)
          </span>
        </div>
      </div>

      <div className="mt-3">
        <DataTable
          tableId="manage-dialers.list:v2"
          columns={columns}
          data={filtered}
          pageSize={10}
          emptyTitle="No PBX found"
          disableExport
        />
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => !next && !deleting && setDeleteOpen(next)}
        title={bulkDeleteMode ? 'Delete selected PBX(s)?' : 'Delete PBX?'}
        description={
          bulkDeleteMode
            ? `${selectedDialers.length} PBX instance(s) will be permanently removed. This action cannot be undone.`
            : 'This PBX instance will be permanently removed. This action cannot be undone.'
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={executeDelete}
      />
    </FillPage>
  );
};

export default DialerListing;
